import {
  type ChatModelRunResult,
  type ExportedMessageRepository,
  fromThreadMessageLike,
  type ThreadMessageLike,
} from '@assistant-ui/react';

import type { Chat } from '../api/types';
import { isReplyInterrupted, isUnanswered } from '../chatState';

import { choiceMessageFromEvent } from './choice';
import { emptyReply, itemOf, type MessageItem, type ReplyItem } from './messageItem';

/** What the builder last said, as it was sent: their message, or a pick (saved as a "Clarified" event)
 *  rebuilt into the message that sent it. This is what "Send it again" sends. */
function lastAsk(chat: Chat): string {
  const last = chat.messages?.at(-1);
  if (!last) return '';
  if (last.role === 'user') return last.text;
  return choiceMessageFromEvent(last.text ?? '') ?? '';
}

/**
 * A saved chat as assistant-ui messages: each builder message, and each reply with the activity lines before
 * it. An answer still owed ends the list as an empty reply (activity so far, or interrupted), and a pending
 * choice rides on the last reply. Used to load a chat's history and when reattaching to a reply mid-write,
 * so both produce the same messages, ids and parent chain.
 */
export function toRepository(chat: Chat): ExportedMessageRepository {
  const messages: { parentId: string | null; like: ThreadMessageLike; id: string }[] = [];
  let parentId: string | null = null;
  let events: string[] = [];
  let lastUserText = '';

  const add = (like: ThreadMessageLike) => {
    const id = `andi-${messages.length}`;
    messages.push({ parentId, like, id });
    parentId = id;
  };
  const withItem = (item: MessageItem) => ({ custom: { item } });

  chat.messages.forEach((message, index) => {
    if (message.role === 'event') {
      events.push(message.text);
    } else if (message.role === 'user') {
      events = [];
      lastUserText = message.text;
      add({
        role: 'user',
        content: [{ type: 'text', text: message.text }],
        metadata: withItem({
          kind: 'user', text: message.text, index, sender: message.sender ?? '',
        }),
      });
    } else {
      const reply = emptyReply({
        events, reply: message.text, index, runId: message.run_id ?? '', prompt: lastUserText,
      });
      events = [];
      add({ role: 'assistant', content: [{ type: 'text', text: message.text }], metadata: withItem(reply) });
    }
  });

  const interrupted = isReplyInterrupted(chat);
  if (events.length || interrupted) {
    const reply = emptyReply({ events, prompt: interrupted ? lastAsk(chat) : '', interrupted });
    add({ role: 'assistant', content: [], metadata: withItem(reply) });
  }

  if (chat.options?.length) {
    const lastReply = messages.map(({ like }) => itemOf(like.metadata)).findLast((item) => item?.kind === 'reply');
    if (lastReply?.kind === 'reply') {
      lastReply.options = chat.options;
      lastReply.question = chat.options_question ?? '';
    }
  }

  const exported = messages.map(({ parentId: parent, like, id }) => ({
    parentId: parent,
    message: fromThreadMessageLike(like, id, { type: 'complete', reason: 'unknown' }),
  }));
  return { headId: exported.at(-1)?.message.id ?? null, messages: exported };
}

/** The saved reply to the chat's last question, or null when it has none yet. Only ever the reply after the
 *  last question: settling on an earlier one would answer a new question with an old reply. */
export function savedReply(chat: Chat): ChatModelRunResult | null {
  if (isUnanswered(chat)) return null;
  const last = toRepository(chat).messages.at(-1)?.message;
  const item = itemOf(last?.metadata);
  if (last?.role !== 'assistant' || item?.kind !== 'reply' || !item.reply) return null;
  const settled: ReplyItem = { ...item, running: false };
  return { content: [{ type: 'text', text: item.reply }], metadata: { custom: { item: settled } } };
}
